import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/core/cross';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { OnboardingWarningBanner } from '@confluence/banners/entry-points/OnboardingWarningBanner.tsx';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import {
	ONBOARDING_STORAGE_ENFORCEMENT_BANNER_DISMISS_EXPERIENCE,
	ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';

import { getStoragePercentForEdition } from '../../storageLimits/storageLimits.ts';
import { BANNER_HEIGHT } from '../constants.ts';
import { useGetCurrentStorageUsage } from '../../hooks/useGetCurrentStorageUsage.ts';

import { SiteAdminDropdownMenu } from './SiteAdminDropdownMenu.tsx';
import { commonMessages } from './i18n.ts';
import { useBannerAnalytics } from './useBannerAnalytics.ts';
import { PlanConsentDeepLink } from './PlanConsentDeepLinkLoadable.tsx';
import { LearnMoreLink } from './LearnMoreLink.tsx';

const i18n = defineMessages({
	closeIcon: {
		id: 'storage-enforcement.icons.close',
		defaultMessage: 'Close icon',
		description:
			'The text is used as an accessibility label for the close icon button in the storage enforcement soft banner. Screen readers announce this label when the user focuses the button to dismiss the banner.',
	},
	softMessageOver: {
		id: 'storage-enforcement.banner.soft.message.over',
		defaultMessage: 'You’ve exceeded the 2GB storage limit for your Free plan.',
		description: 'Description that tells the user that they are over their storage limits',
	},
	softMessageUnderV2: {
		id: 'storage-enforcement.banner.soft.message.under.v2',
		defaultMessage: 'You’ve used <b>{number}%</b> of the Free plan’s 2 GB storage limit.',
		description:
			'Description that tells the user that they are nearing their confluence storage limits.',
	},
	// eslint-disable-next-line @atlassian/i18n/require-description
	upgradeForMoreStorage: {
		id: 'storage-enforcement.banner.button.upgrade-for-more-storage',
		defaultMessage: 'Upgrade for more storage',
	},
});

interface SoftBannerProps {
	onDismiss: () => void; // Used toggle dismiss state
	planConsentDeepLink?: string;
}

export const SoftBanner = ({ onDismiss }: SoftBannerProps) => {
	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { currentUsage } = useGetCurrentStorageUsage();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { fireLearnMoreEvent, fireDismissEvent, fireScreenEvent } = useBannerAnalytics();

	useEffect(() => {
		fireScreenEvent();
	}, [fireScreenEvent]);

	const handleDismiss = () => {
		experienceTracker.start({
			name: ONBOARDING_STORAGE_ENFORCEMENT_BANNER_DISMISS_EXPERIENCE,
		});
		try {
			fireDismissEvent();
			onDismiss();
			experienceTracker.succeed({
				name: ONBOARDING_STORAGE_ENFORCEMENT_BANNER_DISMISS_EXPERIENCE,
			});
		} catch (error) {
			experienceTracker.stopOnError({
				name: ONBOARDING_STORAGE_ENFORCEMENT_BANNER_DISMISS_EXPERIENCE,
				error,
			});
		}
	};

	return (
		<>
			<ExperienceSuccess name={ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE} />
			<OnboardingWarningBanner
				bannerHeight={BANNER_HEIGHT}
				description={formatMessage(i18n.softMessageUnderV2, {
					number: getStoragePercentForEdition(currentUsage, ConfluenceEdition.FREE),
					b: (...chunks: any) => <b>{chunks}</b>,
				})}
				inlineDescriptionCTA={
					isSiteAdmin ? (
						<PlanConsentDeepLink>{formatMessage(i18n.upgradeForMoreStorage)}</PlanConsentDeepLink>
					) : (
						<LearnMoreLink>{formatMessage(commonMessages.learnMoreLink)}</LearnMoreLink>
					)
				}
				rightSideComponent={
					<>
						{isSiteAdmin && (
							<SiteAdminDropdownMenu
								onLearnMoreClick={fireLearnMoreEvent}
								handleDismiss={handleDismiss}
								showDismiss
							/>
						)}
						{!isSiteAdmin && (
							<Button
								appearance="subtle"
								onClick={handleDismiss}
								onMouseDown={(e) => e.preventDefault()}
								iconBefore={<CrossIcon label={formatMessage(i18n.closeIcon)} />}
							/>
						)}
					</>
				}
			/>
		</>
	);
};
